import React, { Suspense, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Delete, Settings } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material'
import { GridToolbarContainer } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'

import OrgAPI from '../../../api/orbitV1/OrgAPI'
import CustomSearchbar from '../../../common/AbstractDataGrid/CustomSearchbar'
import MUIDataGrid from '../../../common/AbstractDataGrid/DataGridMUI'
import PageLayout from '../../../common/Layout/Page/PageLayout'
import LayoutSkeleton from '../../../common/Skeletons/LayoutSkeleton'
import TableSkeleton from '../../../common/Skeletons/TableSkeleton'
import { ColorBoxContent } from '../../../common/ui/colorBox'
import { DeleteDialog } from '../components'

export default function CustomerManagement() {
  const CUSTOMER_LIMIT = 100
  const navigate = useNavigate()
  const [customers, setCustomers] = useState([])
  const [customerId, setCustomerId] = useState('')
  const [customerName, setCustomerName] = useState('')

  /* Dialog Box for deleting user accounts */
  const [isDialogOpen, setDialogOpen] = useState(false)

  const {
    data: retrievedCustomerDetails,
    error: retrievedCustomerDetailsError,
    isLoading,
    isStale,
    refetch
  } = useQuery(['getAllOrgsDetails', 'customerManagement'], async () => {
    const { data } = await OrgAPI.getAllOrgsDetails(CUSTOMER_LIMIT)
    return data
  })

  useEffect(() => {
    if (isStale) refetch()
    if (retrievedCustomerDetails) {
      setCustomers(retrievedCustomerDetails)
    }
  }, [retrievedCustomerDetails])

  if (retrievedCustomerDetailsError) {
    toast.error('Error retrieving customer details. Please try again later!')
  }

  // DATA GRID COMPONENT
  const rows = customers.map((customer) => {
    return {
      name: customer.name,
      _id: customer._id,
      id: customer._id,
      uen: customer.uen,
      email: customer.email,
      deliveryAddress: customer.deliveryAddress,
      billingAddress: customer.billingAddress,
      users: customer.users
    }
  })

  const handleDelete = (id, name) => {
    setCustomerId(id)
    setCustomerName(name)
    setDialogOpen(true)
  }

  const columns = [
    { field: 'name', headerName: 'Name', width: 100 },
    { field: 'uen', headerName: 'UEN', width: 100 },
    {
      field: 'contact-person',
      headerName: 'Contact Person',
      width: 100,
      renderCell: (params) => {
        const customer = params.row
        const rootUser = customer.users[0]
        return rootUser?.name
      }
    },
    {
      field: 'contact-no',
      headerName: 'Contact No.',
      width: 100,
      renderCell: (params) => {
        const customer = params.row
        const rootUser = customer.users[0]
        return rootUser?.contactNumber
      }
    },
    {
      field: 'contact-email',
      headerName: 'Contact Email',
      width: 100,
      renderCell: (params) => {
        const customer = params.row
        const rootUser = customer.users[0]
        return rootUser?.email
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => {
        const customer = params.row
        if (customerId === customer._id) {
          return null
        }
        return (
          <div>
            <IconButton
              component={RouterLink}
              to={`view?customer=${customer._id}`}
              style={{ padding: 0, margin: 0 }}
              data-cy='actionBtn'
            >
              <Settings style={{ color: 'darkblue' }} />
            </IconButton>
            <IconButton
              style={{ padding: 0, margin: 0 }}
              data-cy='actionBtn'
              onClick={() => handleDelete(customer.id, customer.name)}
            >
              <Delete style={{ color: 'darkblue' }} />
            </IconButton>
          </div>
        )
      }
    }
  ]

  const AccountToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <CustomSearchbar />

        <div id='manageCustomers__addUserBtn'>
          <Button
            variant='filled'
            size='small'
            onClick={() => navigate('add')}
            data-cy='newCustomerBtn'
          >
            Add New Customer
          </Button>
        </div>
      </GridToolbarContainer>
    )
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setCustomerId('')
    setCustomerName('')
  }

  const handleConfirmDelete = async () => {
    setDialogOpen(false)
    try {
      const deleteUserResponse = await OrgAPI.deleteOrg(customerId)
      if (deleteUserResponse && deleteUserResponse.data?.message) {
        toast.success('Customer successfully deleted.', {
          autoClose: 3000,
          hideProgressBar: false
        })
        setTimeout(() => {
          refetch()
          setCustomerId('')
          setCustomerName('')
        }, 3000)
      } else {
        toast.error(
          'There was an issue deleting the Customer, please try again later.'
        )
        setCustomerId('')
        setCustomerName('')
      }
    } catch (error) {
      setCustomerId('')
      setCustomerName('')
      throw new Error(error.response.message)
    }
  }

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <PageLayout header={ 'Manage Customers' }>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <ColorBoxContent>
            <MUIDataGrid
              rows={rows}
              columns={columns}
              components={{ Toolbar: AccountToolbar }}
            />
          </ColorBoxContent>
        )}
        <DeleteDialog
          isDialogOpen={isDialogOpen}
          setDialogOpen={closeDialog}
          handleConfirmDelete={handleConfirmDelete}
          customerName={customerName}
        />
      </PageLayout>
    </Suspense>
  )
}
